import React from "react";
import { Grid, Box, Button, Typography } from "@mui/material";
import { Formik, Form, FastField } from "formik";
import * as Yup from "yup";
import { useCompany } from "../../contexts/CompanyContext";
import { createOutbound } from "../../api/outbounds/index.ts";
import { useSnack } from "../../services/SnakbarProvider.js";
import { TextField } from "@mui/material";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import { getWarehouseList } from "../../api/warehouses/index.ts";
import { getPlantList } from "../../api/enterprise/index.js";
import OutboundItems from "../../molecules/Inventory/OutboundItems/index.tsx";
import { useUser } from "../../context/UserContext.js";
import { getUserWarehousesByUserId } from "../../api/userWarehouses/index.js";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import dayjs from "dayjs";

interface OutboundFormProps {
  onSubmit: () => void;
}

const validationSchema = Yup.object().shape({
  outbound: Yup.object().shape({
    warehouseOriginId: Yup.number().required("El almacén de origen es requerido"),
    workOrder: Yup.string(),
    plantDestinationId: Yup.number().required("La planta destino es requerida"),
    requestedBy: Yup.string().required("El solicitante es requerido"),
    deliveredBy: Yup.string().nullable(),
    deliveryDate: Yup.string().nullable(),
    outboundDate: Yup.date().nullable(),
    notes: Yup.string().nullable(),
  }),
  outboundItems: Yup.array()
    .of(
      Yup.object().shape({
        productId: Yup.number().required("El producto es requerido"),
        quantity: Yup.number()
          .required("La cantidad es requerida")
          .min(1, "La cantidad debe ser mayor que 0"),
        workOrder: Yup.string().nullable(),
        description: Yup.string().nullable(),
        weight: Yup.number().nullable(),
      })
    )
    .min(1, "Debe agregar al menos un producto"),
});

const OutboundForm: React.FC<OutboundFormProps> = ({ onSubmit }) => {
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();
  const { userData } = useUser();

  if (!selectedCompany?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Por favor seleccione una compañía primero</Typography>
      </Box>
    );
  }

  const initialValues = {
    outbound: {
      warehouseOriginId: "",
      workOrder: "",
      plantDestinationId: "",
      requestedBy: "",
      deliveredBy: "",
      deliveryDate: "",
      outboundDate: null,
      notes: "",
    },
    outboundItems: [],
  };

  const handleSubmit = async (values: any) => {
    try {
      const formatDate = (date: any) => (date ? dayjs(date).format("YYYY-MM-DD") : null);
      const payload = {
        outbound: {
          ...values.outbound,
          companyId: selectedCompany.id,
          outboundDate: formatDate(values.outbound.outboundDate),
        },
        outboundItems: values.outboundItems,
      };
      await createOutbound(payload);
      openSnack("Salida creada correctamente", "success");
      onSubmit();
    } catch (error) {
      openSnack("Error al crear la salida", "error");
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors }) => (
        <Form>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" gutterBottom>
                  Información General
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FastField
                      name="outbound.warehouseOriginId"
                      as={DropdownSelector}
                      label="Almacén Origen"
                      fetchFunction={getUserWarehousesByUserId}
                      optionLabel={"warehouse.name"}
                      fetchParams={userData.id}
                      error={errors.outbound?.warehouseOriginId}
                      keyField={"warehouseId"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="outbound.workOrder"
                      as={TextField}
                      fullWidth
                      label="Orden de Trabajo"
                      error={!!errors.outbound?.workOrder}
                      helperText={errors.outbound?.workOrder}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="outbound.plantDestinationId"
                      as={DropdownSelector}
                      label="Planta Destino"
                      fetchFunction={getPlantList}
                      optionLabel={"name"}
                      fetchParams={{ companyId: selectedCompany.id }}
                      error={errors.outbound?.plantDestinationId}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="outbound.requestedBy"
                      as={TextField}
                      fullWidth
                      label="Solicitado Por"
                      error={!!errors.outbound?.requestedBy}
                      helperText={errors.outbound?.requestedBy}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="outbound.deliveredBy"
                      as={TextField}
                      fullWidth
                      label="Entregado Por"
                      error={!!errors.outbound?.deliveredBy}
                      helperText={errors.outbound?.deliveredBy}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MyDatePicker
                      label="Fecha de Salida"
                      value={values.outbound.outboundDate}
                      onChange={(date) => setFieldValue("outbound.outboundDate", date)}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FastField
                      name="outbound.notes"
                      as={TextField}
                      fullWidth
                      label="Notas"
                      multiline
                      rows={4}
                      error={!!errors.outbound?.notes}
                      helperText={errors.outbound?.notes}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={8}>
                <OutboundItems
                  formMode="insert"
                  companyId={selectedCompany.id}
                />
                {errors.outboundItems && typeof errors.outboundItems === 'string' && (
                  <Typography color="error" sx={{ mt: 1 }}>
                    {errors.outboundItems}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                >
                  <Button type="submit" variant="contained" color="primary">
                    Guardar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default OutboundForm;
